/**
 * ImpersonationBanner
 * Displays a fixed warning banner when a super_admin is impersonating a user
 * Provides a one-click button to stop impersonating
 */
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import { useCurrentUser } from "@/hooks/use-current-user";
import { useStopImpersonating } from "@/hooks/admin/use-impersonation";

export function ImpersonationBanner() {
  const { data: currentUser } = useCurrentUser();
  const stopImpersonating = useStopImpersonating();

  // Only show banner when impersonation is active
  if (!currentUser?.impersonatedBy) {
    return null;
  }

  return (
    <Alert
      severity="warning"
      sx={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        borderRadius: 0,
        py: 0.5,
        // Responsive layout: stack on mobile
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        "& .MuiAlert-message": {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: { xs: "100%", sm: "auto" },
        },
        "& .MuiAlert-action": {
          ml: { xs: 0, sm: 1 },
          mt: { xs: 0.5, sm: 0 },
          pl: 0,
        },
      }}
      action={
        <Button
          color="inherit"
          size="small"
          onClick={() => stopImpersonating.mutate()}
          disabled={stopImpersonating.isPending}
        >
          {stopImpersonating.isPending ? "Stopping..." : "Stop Impersonating"}
        </Button>
      }
    >
      You are viewing as: <strong>{currentUser.email}</strong>
    </Alert>
  );
}
